/**
 * Theme Utilities
 * Helper functions for theme management and CSS variable manipulation
 */

import type { ThemeColors, ThemeMode, ColorScheme, CompleteTheme } from './types';
import { getTheme } from './themes';

/**
 * Storage keys for theme persistence
 */
export const THEME_STORAGE_KEYS = {
  MODE: 'wciq-theme-mode',
} as const;

/**
 * Detect system color scheme preference
 */
export function getSystemColorScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve the actual theme mode based on user preference and system setting
 */
export function resolveThemeMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemColorScheme();
  }
  return mode;
}

/**
 * Convert theme colors object to CSS custom properties
 */
export function themeColorsToCSS(colors: ThemeColors): Record<string, string> {
  return {
    '--background': colors.background,
    '--foreground': colors.foreground,
    '--card': colors.card,
    '--card-foreground': colors.cardForeground,
    '--popover': colors.popover,
    '--popover-foreground': colors.popoverForeground,
    '--primary': colors.primary,
    '--primary-foreground': colors.primaryForeground,
    '--secondary': colors.secondary,
    '--secondary-foreground': colors.secondaryForeground,
    '--muted': colors.muted,
    '--muted-foreground': colors.mutedForeground,
    '--accent': colors.accent,
    '--accent-foreground': colors.accentForeground,
    '--destructive': colors.destructive,
    '--destructive-foreground': colors.destructiveForeground,
    '--border': colors.border,
    '--input': colors.input,
    '--ring': colors.ring,
    '--chart-1': colors.chart1,
    '--chart-2': colors.chart2,
    '--chart-3': colors.chart3,
    '--chart-4': colors.chart4,
    '--chart-5': colors.chart5,
    '--sidebar': colors.sidebar,
    '--sidebar-foreground': colors.sidebarForeground,
    '--sidebar-primary': colors.sidebarPrimary,
    '--sidebar-primary-foreground': colors.sidebarPrimaryForeground,
    '--sidebar-accent': colors.sidebarAccent,
    '--sidebar-accent-foreground': colors.sidebarAccentForeground,
    '--sidebar-border': colors.sidebarBorder,
    '--sidebar-ring': colors.sidebarRing,
    '--success': colors.success,
    '--success-foreground': colors.successForeground,
    '--warning': colors.warning,
    '--warning-foreground': colors.warningForeground,
    '--info': colors.info,
    '--info-foreground': colors.infoForeground,
  };
}

/**
 * Apply theme colors to document root
 */
export function applyThemeToRoot(colors: ThemeColors): void {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  const cssVars = themeColorsToCSS(colors);
  
  Object.entries(cssVars).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
}

/**
 * Get theme class name for applying to root element
 */
export function getThemeClassName(mode: 'light' | 'dark', colorScheme: ColorScheme): string {
  const modeClass = mode === 'dark' ? 'dark' : '';
  const schemeClass = colorScheme !== 'default' ? `theme-${colorScheme}` : '';
  
  return [modeClass, schemeClass].filter(Boolean).join(' ');
}

/**
 * Apply complete theme to document
 */
export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  
  const theme = getTheme('default');
  const resolvedMode = resolveThemeMode(mode);
  const colors = resolvedMode === 'dark' ? theme.dark : theme.light;
  
  // Apply CSS variables
  applyThemeToRoot(colors);
  
  // Apply theme classes
  const themeClass = getThemeClassName(resolvedMode, 'default');
  document.documentElement.className = themeClass;
}

/**
 * Save theme preferences to localStorage
 */
export function saveThemePreferences(mode: ThemeMode): void {
  if (typeof localStorage === 'undefined') return;
  
  localStorage.setItem(THEME_STORAGE_KEYS.MODE, mode);
}

/**
 * Load theme preferences from localStorage
 */
export function loadThemePreferences(): { mode: ThemeMode } {
  if (typeof localStorage === 'undefined') {
    return { mode: 'system' };
  }
  
  const mode = (localStorage.getItem(THEME_STORAGE_KEYS.MODE) as ThemeMode) || 'system';
  
  return { mode };
}

/**
 * Listen for system color scheme changes
 */
export function watchSystemColorScheme(callback: (isDark: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  
  const handler = (e: MediaQueryListEvent) => {
    callback(e.matches);
  };
  
  mediaQuery.addEventListener('change', handler);
  
  // Return cleanup function
  return () => mediaQuery.removeEventListener('change', handler);
}

/**
 * Get chart colors for current theme and mode
 */
export function getChartColors(theme: CompleteTheme, mode: 'light' | 'dark') {
  return theme.chartConfig[mode].colors;
}

/**
 * Get financial colors for current theme and mode
 */
export function getFinancialColors(theme: CompleteTheme, mode: 'light' | 'dark') {
  return theme.financialColors[mode];
}

/**
 * Get asset colors for current theme and mode
 */
export function getAssetColors(theme: CompleteTheme, mode: 'light' | 'dark') {
  return theme.assetColors[mode];
}

/**
 * Create theme-aware CSS class generator
 */
export function createThemeClassNames(baseClasses: string, themeClasses: Record<ColorScheme, string>) {
  return (colorScheme: ColorScheme) => {
    const themeClass = themeClasses[colorScheme] || themeClasses.default;
    return `${baseClasses} ${themeClass}`.trim();
  };
}

/**
 * Validate color scheme
 */
export function isValidColorScheme(scheme: string): scheme is ColorScheme {
  const validSchemes: ColorScheme[] = [
    'default', 'blue', 'green', 'purple', 'orange', 'red', 'emerald', 'violet', 'rose', 'amber'
  ];
  return validSchemes.includes(scheme as ColorScheme);
}

/**
 * Validate theme mode
 */
export function isValidThemeMode(mode: string): mode is ThemeMode {
  const validModes: ThemeMode[] = ['light', 'dark', 'system'];
  return validModes.includes(mode as ThemeMode);
}

/**
 * Generate theme preview colors (for theme selector)
 */
export function getThemePreviewColors(theme: CompleteTheme) {
  return {
    light: {
      primary: theme.light.primary,
      secondary: theme.light.secondary,
      accent: theme.light.accent,
      background: theme.light.background,
    },
    dark: {
      primary: theme.dark.primary,
      secondary: theme.dark.secondary,
      accent: theme.dark.accent,
      background: theme.dark.background,
    }
  };
}

